import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { DataService } from '@services/contests-data.service';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { firstValueFrom, timeout, catchError, throwError } from 'rxjs';
import { AuthService } from 'src/app/Auth/AuthService';
import { HasUnsavedChanges } from 'src/app/Auth/unsaved-changes.guard';
import { NgZorroAntdModule } from 'src/app/ng-zorro-antd.module';
import { QRCodeComponent } from 'angularx-qrcode';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-add-contents',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NzSelectModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgZorroAntdModule,
    NzDatePickerModule,
    QRCodeComponent,
  ],
  templateUrl: './add-contents.component.html',
  styleUrls: ['./add-content.scss'],
})
export class AddContentsComponent implements HasUnsavedChanges, OnDestroy {
  private readonly leavePageWarningMessage =
    'Are you sure you want to leave this page? Unsaved changes will be lost.';

  bannerPreviews: string[] = [];
  activeBannerIndex: number = 0;
  imagePreview: any[] = [];
  @ViewChildren('qrcode') qrCodeElements!: QueryList<ElementRef>;
  addContentForm: FormGroup;
  sharedService = inject(SharedService);
  countryRecords: any;
  countriesLoaded: boolean = false;
  allCountriesSelected: boolean = false;
  categoriesList: any[] = [];
  categoriesTree: any[] = [];
  prizeCategoryOptions: any[][] = [];
  categoriesLoaded: boolean = false;
  quizzesList: any[] = [];
  quizzesLoaded: boolean = false;
  isVisible = false;
  singleContestData: any;
  isVisiblesubmit = false;
  @ViewChild('profileFileInput')
  profileFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('prizeFileInput') prizeFileInput!: ElementRef<HTMLInputElement>;
  timelineDays: number = 1;
  progressWidth: number = 14.3;
  modalForm!: FormGroup;
  isEditMode: boolean = false;
  contestID: any;
  levelQrCodes: (string | null)[] = [];
  levelQrCodesOriginal: (string | null)[] = []; // Track original QR data to detect changes
  levelImagePreviews: (string | ArrayBuffer | null)[] = [];
  ageRanges: string[] = [];
  isAccessCodeModalVisible = false;
  accessCode: string | null = null;
  isCodeCopied = false;
  isQrVisible = false;
  isImageVisible: boolean[] = []; // Array to track image modal visibility for each level
  isCustomInput: boolean = false;
  isSavingDraft: boolean = false;
  currentStep: number = 1; // Stepper current step
  selectedLevelCount: number | null = null; // Selected level count from buttons
  customLevelCount: number | null = null; // Custom level count from input
  clientLogo: string = 'assets/images/luqta_favicon2.png'; // Client logo for preview
  contestTimeLeft: string = '0d 0h 0m 0s';
  private contestCountdownIntervalId: ReturnType<typeof setInterval> | null =
    null;
  private levelDateOrderValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const errors: ValidationErrors = {};

    const start = control.get('level_start_date')?.value;
    const expiry = control.get('level_timeline')?.value;
    if (start && expiry) {
      const startTime = new Date(start).getTime();
      const expiryTime = new Date(expiry).getTime();
      if (startTime >= expiryTime) {
        errors['levelDateOrder'] = true;
      }
    }

    const type = control.get('type')?.value;
    const clientVariables = control.get('client_variables') as FormArray | null;
    if (type === 'client_webhook') {
      if (!clientVariables || clientVariables.length === 0) {
        errors['clientVariablesRequired'] = true;
      } else {
        const hasIncompleteVariable = clientVariables.controls.some((variableCtrl) => {
          const variableGroup = variableCtrl as FormGroup;
          const variableName = String(variableGroup.get('variable_name')?.value ?? '').trim();
          const displayName = String(variableGroup.get('display_name')?.value ?? '').trim();
          const dataType = String(variableGroup.get('datatype')?.value ?? '').trim();
          return !variableName || !displayName || !dataType;
        });

        if (hasIncompleteVariable) {
          errors['clientVariablesIncomplete'] = true;
        }
      }
    }

    return Object.keys(errors).length ? errors : null;
  };

  ngOnInit() {
    // Clear any stuck loader from previous pages
    this.sharedService.hideLoader();

    // Get client logo from localStorage
    const storedImage = localStorage.getItem('profileImage');
    if (storedImage && storedImage !== 'assets/images/profile.png') {
      this.clientLogo = storedImage;
    }

    this.isSavingDraft = false;
    this.subscribeToPointsChanges();
    // this.addContentForm.get('totallevels')?.valueChanges.subscribe((value) => {
    //   this.updateLevels(value);
    // });

    this.subscribeToContestDateChanges();
    this.startContestCountdown();

    const contestData = history.state.contest;
    const aiContestData = history.state.aiContestData;
    const aiGeneratedAssets = history.state.aiGeneratedAssets;

    // Only add default level if NOT loading AI data
    if (this.levels.length === 0 && !aiContestData) {
      this.addNewLevel();
    }
    this.prizeArray.push(this.createPrize());
    this.prizeCategoryOptions.push(this.cloneCategoriesTree());


    const encodedUid = this.route.snapshot.queryParamMap.get('id');

    if (encodedUid) {
      const storedId = localStorage.getItem(encodedUid);
      if (storedId) {
        this.contestID = Number(storedId);
        this.isEditMode = true;
        this.getContestDetails(this.contestID);
      }
    }

    if (!this.isEditMode && aiContestData) {
      // Load countries and categories first, then apply AI data
      Promise.all([
        this.getCountryRecords(),
        this.loadCategoriesAsync()
      ]).then(() => {
        this.applyAiContestData(aiContestData, aiGeneratedAssets);
      });
    }


    this.ageRanges = [];
    this.ageRanges.push('All');

    const ranges = [
      { start: 18, end: 25 },
      { start: 26, end: 35 },
      { start: 36, end: 45 },
      { start: 46, end: 55 },
      { start: 56, end: 65 },
    ];

    ranges.forEach((r) => {
      this.ageRanges.push(`${r.start}-${r.end}`);
    });
    this.ageRanges.push('65+');


    this.addContentForm.get('format_of_contest')?.valueChanges.subscribe(value => {
      this.onFormatChange(value);
    });
  }

  private applyAiContestData(rawData: any, assets?: { banner?: string; prizes?: string[]; levels?: string[] }): void {
    const data = rawData?.data && typeof rawData.data === 'object' ? rawData.data : rawData;
    if (!data || typeof data !== 'object') {
      return;
    }

    const name = String(data.name ?? data.title ?? '').trim();
    const details = String(data.details ?? data.description ?? data.desc ?? '').trim();
    const arabicName = String(data.arabic_name ?? name).trim();
    const arabicDetails = String(data.arabic_details ?? details).trim();

    const location = this.normalizeAiLocation(data.location ?? data.country);
    const age = this.normalizeAiAge(data.age);

    const startDate = this.parseAiDate(data.start_date);
    const endDate = this.parseAiDate(data.end_date);
    const prizeDate = this.parseAiDate(data.prize_announce_date);

    this.addContentForm.patchValue({
      name,
      arabic_name: arabicName,
      details,
      arabic_details: arabicDetails,
      gender: data.gender ?? 'both',
      age,
      location,
      number_of_winners: data.number_of_winners ?? data.winners ?? data.winner_count ?? '',
      type: data.type ?? data.contest_type ?? data.contestType ?? '',
      sharingSetting: data.sharingSetting ?? data.sharing_setting ?? '',
      format_of_contest: data.format_of_contest ?? data.format ?? '',
    }, { emitEvent: false });

    if (startDate || endDate) {
      this.addContentForm.patchValue({
        start_date: startDate ?? null,
        end_date: endDate ?? null,
      }, { emitEvent: false });
    }
    if (prizeDate) {
      this.addContentForm.patchValue({
        prize_announce_date: prizeDate
      }, { emitEvent: false });
    }

    this.applyAiBanners(assets?.banner ?? data.banner ?? data.banner_url ?? '');

    // Extract category_id from contest level and apply to prizes
    const contestCategoryId = data.category_id ?? null;
    this.applyAiPrizes(
      data._contest_prize ?? data.prizes ?? data.prize ?? [],
      assets?.prizes ?? [],
      contestCategoryId
    );
    this.applyAiLevels(
      data._levels_of_contest ?? data.levels ?? [],
      assets?.levels ?? []
    );
  }

  private applyAiBanners(banner: string): void {
    if (!banner) return;
    this.bannerPreviews = [banner];
    const bannersArray = this.addContentForm.get('banners_code') as FormArray;
    bannersArray.clear();
    bannersArray.push(this.fb.control(banner));
    this.activeBannerIndex = 0;
  }

  private applyAiPrizes(prizesRaw: any[], prizeImages: string[], contestCategoryId: number | null = null): void {
    const prizes = Array.isArray(prizesRaw) ? prizesRaw : [];
    this.prizes.clear();
    this.prizeCategoryOptions = [];
    this.imagePreview = [];

    if (prizes.length === 0) {
      this.prizeArray.push(this.createPrize());
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      return;
    }

    prizes.forEach((prize, index) => {
      const imageCode = prize?.image?.path || prize?.image?.url || prizeImages[index] || '';

      // Priority: prize.categories > contestCategoryId > empty
      let categoryValue: number[][] = [];
      if (prize?.categories && Array.isArray(prize.categories) && prize.categories.length > 0) {
        // Use prize-level categories if present
        categoryValue = this.categoryIdsToCascaderValue(prize.categories);
      } else if (contestCategoryId) {
        // Otherwise, use contest-level category_id
        categoryValue = this.categoryIdsToCascaderValue([contestCategoryId]);
      }

      const prizeGroup = this.fb.group({
        name: [prize?.name || `Prize ${index + 1}`, Validators.required],
        value: [prize?.value ?? prize?.amount ?? '', [Validators.required, Validators.min(0)]],
        image_code: [imageCode, Validators.required],
        category_type: [categoryValue, Validators.required],
        id: [prize?.id ?? null],
      });

      this.prizes.push(prizeGroup);
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      this.imagePreview[index] = { image_code: imageCode };
    });
  }

  private applyAiLevels(levelsRaw: any[], levelImages: string[] = []): void {
    const levels = Array.isArray(levelsRaw) ? levelsRaw : [];
    if (levels.length === 0) return;

    console.log('applyAiLevels called with:', levels.length, 'levels and', levelImages.length, 'images');
    console.log('Level images:', levelImages);

    const normalizedLevels = levels.map((level: any, index: number) => {
      const levelName = level?.name || `Level ${index + 1}`;
      const levelDescription = level?.description ?? level?.details ?? '';

      // Use AI-generated image if available, otherwise use level's image
      const levelImage = levelImages[index] ?? level?.image ?? level?.image_url ?? null;
      console.log(`Level ${index} (${level?.level_type}):`, levelName, 'Image:', levelImage ? levelImage.substring(0, 50) + '...' : 'none');

      return {
        id: level?.id ?? null,
        level_type: level?.level_type ?? level?.type ?? 'text',
        type: level?.level_type ?? level?.type ?? 'text',
        name: levelName,
        arabic_name: level?.arabic_name ?? levelName,
        description: levelDescription,
        arabic_description: level?.arabic_description ?? levelDescription,
        textContent: level?.textContent ?? level?.text_content ?? level?.qrData ?? '',
        image: levelImage,
        level_start_date: level?.level_start_date ?? level?.start_date ?? null,
        level_timeline: level?.level_timeline ?? level?.timeline ?? null,
        client_webhook_url: level?.client_webhook_url ?? '',
        luqta_webhook_url: level?.luqta_webhook_url ?? '',
        client_variables: level?.client_variables ?? [],
        quizzes_id: level?.quizzes_id ?? null,
        accept_any_answer: level?.accept_any_answer ?? false,
      };
    });

    this.setLevelsData(normalizedLevels);

    // Set level count with a small delay to ensure UI updates
    setTimeout(() => {
      this.addContentForm.get('totallevels')?.setValue(normalizedLevels.length, { emitEvent: true });
    }, 0);
  }

  private normalizeAiLocation(value: any): string[] {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      return value.split(',').map((v) => v.trim()).filter(Boolean);
    }
    return [];
  }

  private normalizeAiAge(value: any): string[] {
    if (!value) return ['All'];
    if (Array.isArray(value)) return value;
    return [String(value)];
  }

  private parseAiDate(value: any): Date | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  navigateBack(): void {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    this.router.navigate(['/home/contest-type']);
  }

  onLocationDropdownOpen(isOpen: boolean) {
    if (isOpen && !this.countriesLoaded) {
      this.getCountryRecords();
    }
    if (isOpen) {
      this.updateAllCountriesSelectedState();
    }
  }

  async getCountryRecords() {
    if (this.countriesLoaded) return Promise.resolve();
    const data = await this.sharedService.getCountryRecords();
    this.countryRecords = data;
    this.countriesLoaded = true;
    this.updateAllCountriesSelectedState();
  }

  onSelectAllCountries(selectAll: boolean) {
    if (selectAll && this.countryRecords) {
      const allCountryNames = this.countryRecords.map((c: any) => c.name);
      this.addContentForm.get('location')?.setValue(allCountryNames);
    } else {
      this.addContentForm.get('location')?.setValue([]);
    }
  }

  updateAllCountriesSelectedState() {
    const selectedLocations = this.addContentForm.get('location')?.value || [];
    this.allCountriesSelected = this.countryRecords &&
      this.countryRecords.length > 0 &&
      selectedLocations.length === this.countryRecords.length;
  }

  onCategoryDropdownOpen(isOpen: boolean) {
    if (isOpen && !this.categoriesLoaded) {
      this.getCategoriesRecords();
    }
  }

  getCategoriesRecords() {
    if (this.categoriesLoaded) return;
    this.authService.getAll('CATEGORIES_LOOKUP').subscribe({
      next: (response: any) => {
        this.categoriesList = response;
        this.categoriesTree = this.buildCategoryTree(response);
        this.categoriesLoaded = true;
        this.ensurePrizeCategoryOptions();
      },
      error: (error) => {
        this.sharedService.createNotification(
          'error',
          'Failed to load categories data'
        );
      },
    });
  }

  loadCategoriesAsync(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.categoriesLoaded) {
        resolve();
        return;
      }
      this.authService.getAll('CATEGORIES_LOOKUP').subscribe({
        next: (response: any) => {
          this.categoriesList = response;
          this.categoriesTree = this.buildCategoryTree(response);
          this.categoriesLoaded = true;
          this.ensurePrizeCategoryOptions();
          resolve();
        },
        error: (error) => {
          this.sharedService.createNotification(
            'error',
            'Failed to load categories data'
          );
          reject(error);
        },
      });
    });
  }

  buildCategoryTree(flatList: any[]): any[] {
    const map = new Map<number, any>();
    const roots: any[] = [];

    // Create nodes with cascader format
    flatList.forEach(item => {
      map.set(item.id, {
        value: item.id,
        label: item.name,
        children: [],
        isLeaf: true
      });
    });

    // Build hierarchy
    flatList.forEach(item => {
      const node = map.get(item.id);
      if (item.parent_id === 0) {
        roots.push(node);
      } else {
        const parent = map.get(item.parent_id);
        if (parent) {
          parent.children.push(node);
          parent.isLeaf = false;
        }
      }
    });

    // Remove empty children arrays for leaf nodes
    const cleanLeaves = (nodes: any[]) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanLeaves(node.children);
        }
      });
    };
    cleanLeaves(roots);

    return roots;
  }

  // Extract category IDs from cascader multi-select value
  // Cascader returns [[id1, id2, id3], [id4, id5]] - we need the last ID of each path
  extractCategoryIds(cascaderValue: any): number[] | null {
    if (!cascaderValue || !Array.isArray(cascaderValue) || cascaderValue.length === 0) {
      return null;
    }
    // For multiselect, each item is an array representing the path
    return cascaderValue.map((path: any): number => {
      if (Array.isArray(path) && path.length > 0) {
        return path[path.length - 1] as number; // Get the last ID (the selected category)
      }
      return path as number; // Fallback if it's already a single value
    });
  }

  // Build cascader path for a category ID (for edit mode)
  buildCategoryPath(categoryId: number): number[] {
    const path: number[] = [];
    let currentId = categoryId;

    while (currentId) {
      path.unshift(currentId);
      const category = this.categoriesList.find(c => c.id === currentId);
      if (category && category.parent_id !== 0) {
        currentId = category.parent_id;
      } else {
        break;
      }
    }
    return path;
  }

  // Convert saved category IDs to cascader format for edit mode
  categoryIdsToCascaderValue(categoryIds: number[] | number | null): number[][] {
    if (!categoryIds) return [];

    const ids = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
    return ids.map(id => this.buildCategoryPath(id));
  }

  private cloneCategoriesTree(): any[] {
    return this.categoriesTree?.length
      ? JSON.parse(JSON.stringify(this.categoriesTree))
      : [];
  }

  private ensurePrizeCategoryOptions(): void {
    const totalPrizes = this.prizes?.length || 0;
    for (let i = 0; i < totalPrizes; i++) {
      const currentOptions = this.prizeCategoryOptions[i];
      // If row options are missing or were created before categories loaded, refresh them.
      if (!Array.isArray(currentOptions) || currentOptions.length === 0) {
        this.prizeCategoryOptions[i] = this.cloneCategoriesTree();
      }
    }
    if (this.prizeCategoryOptions.length > totalPrizes) {
      this.prizeCategoryOptions.length = totalPrizes;
    }
  }

  onQuizDropdownOpen(isOpen: boolean) {
    if (isOpen && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }
  }

  getQuizzesRecords() {
    if (this.quizzesLoaded) return;
    this.authService.getAll('GET_QUIZZES_LOOKUP').subscribe({
      next: (response: any) => {
        this.quizzesList = response || [];
        this.quizzesLoaded = true;
      },
      error: () => {
        this.sharedService.createNotification(
          'error',
          'Failed to load quizzes data'
        );
      },
    });
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private location: Location,
    private route: ActivatedRoute,
    private pagesService: PagesService,
    private authService: AuthService,
    private modal: NzModalService,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
  ) {
    this.addContentForm = this.fb.group({
      banners_code: this.fb.array([]), // now a FormArray
      name: ['', Validators.required],
      arabic_name: ['', Validators.required],
      arabic_details: ['', Validators.required],

      age: [['All'], Validators.required],
      details: ['', [Validators.required, Validators.maxLength(1000)]],


      gender: ['both', Validators.required],
      // game_name: ['', Validators.required],
      number_of_winners: [
        '',
        [Validators.required, Validators.min(1)]
      ],
      // no_of_users: ['', Validators.required],
      location: [[], Validators.required],
      type: ['', Validators.required],
      sharingSetting: ['', Validators.required],
      // total_points: ['', Validators.required],
      start_date: [null, Validators.required],
      end_date: [null, Validators.required],
      prize_announce_date: ['', Validators.required],
      format_of_contest: ['', Validators.required], //formate of contest
      totallevels: [''],
      levels_of_contest: this.fb.array([]),
      _contest_prize: this.fb.array([]),
    });

    this.initModalForm();
    // Clear the images on window reload
    window.onbeforeunload = () => {
      sessionStorage.removeItem('banners_code');
      sessionStorage.removeItem('image_code');
    };
    this.addContentForm.get('start_date')?.valueChanges.subscribe((value) => {
      this.updateEndDateState(value);

      const endDateControl = this.addContentForm.get('end_date');
      const prizeDateControl = this.addContentForm.get('prize_announce_date');
      const endDate = endDateControl?.value;

      // If start date is cleared/deleted, clear both end date and prize date
      if (!value) {
        if (endDate) {
          endDateControl?.setValue(null);
          endDateControl?.markAsTouched();
        }
        if (prizeDateControl?.value) {
          prizeDateControl?.setValue(null);
          prizeDateControl?.markAsTouched();
        }
        return;
      }

      // If start date has a value, validate end date (must be after start date)
      if (endDate) {
        const start = new Date(value);
        const end = new Date(endDate);

        // If end date is not after start date, clear it and mark as touched
        if (end.getTime() <= start.getTime()) {
          endDateControl?.setValue(null);
          endDateControl?.markAsTouched();
        }
      }
    });

    // Subscribe to end_date changes to validate prize_announce_date
    this.addContentForm.get('end_date')?.valueChanges.subscribe((value) => {
      this.updateContestTimeLeft();
      const prizeDateControl = this.addContentForm.get('prize_announce_date');
      const prizeDate = prizeDateControl?.value;

      // If end date is cleared/deleted, clear prize date
      if (!value) {
        if (prizeDate) {
          prizeDateControl?.setValue(null);
          prizeDateControl?.markAsTouched();
        }
        return;
      }

      // If end date has a value, validate prize date (must be after end date)
      if (prizeDate) {
        const end = new Date(value);
        const prize = new Date(prizeDate);

        // If prize date is not after end date, clear it and mark as touched
        if (prize.getTime() <= end.getTime()) {
          prizeDateControl?.setValue(null);
          prizeDateControl?.markAsTouched();
        }
      }
    });
  }

  addNewLevel(): void {
    const newLevelGroup = this.fb.group({
      id: [null],
      type: [null, Validators.required],
      name: ['', Validators.required],
      arabic_name: ['', Validators.required],
      description: ['', [Validators.required, Validators.maxLength(1000)]],
      arabic_description: ['', [Validators.required, Validators.maxLength(1000)]],
      level_start_date: [null],
      level_timeline: [null, Validators.required],
      textContent: this.fb.array([this.fb.control('', [Validators.maxLength(1000)])]),
      imageFile: [null],
      qrData: [''],
      client_webhook_url: [''],
      luqta_webhook_url: [''],
      accept_any_answer: [false],
      client_variables: this.fb.array([]),
      quizzes_id: [null],
    }, { validators: this.levelDateOrderValidator });

    const newIndex = this.levels.length;
    this.subscribeToLevelChanges(newLevelGroup, newIndex);
    this.levels.push(newLevelGroup);
    this.levelQrCodes.push(null);
    this.levelImagePreviews.push(null);
    this.isImageVisible.push(false); // Initialize image modal visibility
  }


  // Helper method to generate range of numbers
  private range(start: number, end: number): number[] {
    const result: number[] = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  }

  // Helper method to check if two dates are on the same day
  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
  private updateEndDateState(startDate: Date | null): void {
    const endDateControl = this.addContentForm.get('end_date');
    if (startDate) {
      endDateControl?.enable();
    } else {
      endDateControl?.disable();
    }
    if (startDate) {
      endDateControl?.setValidators([Validators.required]);
    } else {
      endDateControl?.clearValidators();
    }
    endDateControl?.updateValueAndValidity();
  }
  getDisabledStartDate = (current: Date): boolean => {
    const now = new Date();
    // Set both to midnight for date-only comparison
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);
    const todayDateOnly = new Date(now);
    todayDateOnly.setHours(0, 0, 0, 0);

    // Disable dates before today (not including today)
    return currentDateOnly.getTime() < todayDateOnly.getTime();
  };

  getDisabledStartDateTime = (current: Date | Date[]) => {
    const now = new Date();

    // Handle array case (for range pickers)
    const currentDate = Array.isArray(current) ? current[0] : current;

    // If no current date provided, return no restrictions
    if (!currentDate) {
      return {
        nzDisabledHours: () => [],
        nzDisabledMinutes: () => [],
        nzDisabledSeconds: () => []
      };
    }

    return {
      nzDisabledHours: () => {
        const currentHour = now.getHours();
        // If selecting today, disable hours before current hour
        if (this.isSameDay(currentDate, now)) {
          return this.range(0, currentHour);
        }
        return [];
      },
      nzDisabledMinutes: (hour: number) => {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        // If selecting today and current hour, disable minutes before current minute
        if (this.isSameDay(currentDate, now) && hour === currentHour) {
          return this.range(0, currentMinute);
        }
        return [];
      },
      nzDisabledSeconds: (hour: number, minute: number) => {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentSecond = now.getSeconds();
        // If selecting today, current hour and current minute, disable seconds before current second
        if (this.isSameDay(currentDate, now) && hour === currentHour && minute === currentMinute) {
          return this.range(0, currentSecond + 1); // +1 to ensure future time
        }
        return [];
      }
    };
  };
  getDisabledPrizeDate = (current: Date): boolean => {
    const endDate = this.addContentForm.get('end_date')?.value;

    if (!endDate) return true;

    const end = new Date(endDate);

    // Set to midnight for date-only comparison
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(end);
    endDateOnly.setHours(0, 0, 0, 0);

    // Allow same day as end date, disable dates before end date
    return currentDateOnly.getTime() < endDateOnly.getTime();
  };

  getDisabledPrizeDateTime = (current: Date | Date[]) => {
    const endDate = this.addContentForm.get('end_date')?.value;

    // Handle array case (for range pickers)
    const currentDate = Array.isArray(current) ? current[0] : current;

    if (!endDate || !currentDate) {
      return {
        nzDisabledHours: () => this.range(0, 24),
        nzDisabledMinutes: () => this.range(0, 60),
        nzDisabledSeconds: () => this.range(0, 60)
      };
    }

    const end = new Date(endDate);

    // Normalize current date for comparison
    const currentDateOnly = new Date(currentDate);
    currentDateOnly.setHours(0, 0, 0, 0);
    const endDateOnly = new Date(end);
    endDateOnly.setHours(0, 0, 0, 0);

    return {
      nzDisabledHours: () => {
        // On same day as end date, disable hours before end hour
        if (currentDateOnly.getTime() === endDateOnly.getTime()) {
          return this.range(0, end.getHours());
        }
        return [];
      },
      nzDisabledMinutes: (hour: number) => {
        // On same day and same hour as end date, disable minutes before end minute
        if (currentDateOnly.getTime() === endDateOnly.getTime() && hour === end.getHours()) {
          return this.range(0, end.getMinutes() + 1);
        }
        return [];
      },
      nzDisabledSeconds: (hour: number, minute: number) => {
        // On same day, same hour, same minute as end date, disable seconds before end second
        if (currentDateOnly.getTime() === endDateOnly.getTime() &&
          hour === end.getHours() &&
          minute === end.getMinutes()) {
          return this.range(0, end.getSeconds() + 1);
        }
        return [];
      }
    };
  };
  getDisabledEndDate = (current: Date): boolean => {
    const startDate = this.addContentForm.get('start_date')?.value;

    if (!startDate) return true;

    const start = new Date(startDate);

    // Set to midnight for date-only comparison
    const currentDateOnly = new Date(current);
    currentDateOnly.setHours(0, 0, 0, 0);
    const startDateOnly = new Date(start);
    startDateOnly.setHours(0, 0, 0, 0);

    // Allow same day as start date, disable dates before start date
    return currentDateOnly.getTime() < startDateOnly.getTime();
  };

  getDisabledEndDateTime = (current: Date | Date[]) => {
    const startDate = this.addContentForm.get('start_date')?.value;

    // Handle array case (for range pickers)
    const currentDate = Array.isArray(current) ? current[0] : current;

    if (!startDate || !currentDate) {
      return {
        nzDisabledHours: () => this.range(0, 24),
        nzDisabledMinutes: () => this.range(0, 60),
        nzDisabledSeconds: () => this.range(0, 60)
      };
    }

    const start = new Date(startDate);

    // Normalize current date for comparison
    const currentDateOnly = new Date(currentDate);
    currentDateOnly.setHours(0, 0, 0, 0);
    const startDateOnly = new Date(start);
    startDateOnly.setHours(0, 0, 0, 0);

    return {
      nzDisabledHours: () => {
        const disabled: number[] = [];

        // On same day as start date, disable hours before start hour
        if (currentDateOnly.getTime() === startDateOnly.getTime()) {
          disabled.push(...this.range(0, start.getHours()));
        }

        return disabled;
      },
      nzDisabledMinutes: (hour: number) => {
        const disabled: number[] = [];

        // On same day and same hour as start, disable minutes before start minute
        if (currentDateOnly.getTime() === startDateOnly.getTime() && hour === start.getHours()) {
          disabled.push(...this.range(0, start.getMinutes() + 1));
        }

        return disabled;
      },
      nzDisabledSeconds: (hour: number, minute: number) => {
        const disabled: number[] = [];

        // On same day, same hour, same minute as start, disable seconds before start second
        if (currentDateOnly.getTime() === startDateOnly.getTime() &&
          hour === start.getHours() &&
          minute === start.getMinutes()) {
          disabled.push(...this.range(0, start.getSeconds() + 1));
        }

        return disabled;
      }
    };
  };
  getDisabledPrizeStartDate = (current: Date): boolean => {
    const contestStartRaw = this.addContentForm.get('start_date')?.value;
    const contestEndRaw = this.addContentForm.get('end_date')?.value;
    if (!contestStartRaw || !contestEndRaw) return true;
    const contestStart = new Date(contestStartRaw);
    const contestEnd = new Date(contestEndRaw);
    // Convert current to midnight for reliable comparison
    const currentDate = new Date(current.setHours(0, 0, 0, 0));
    contestStart.setHours(0, 0, 0, 0);
    contestEnd.setHours(0, 0, 0, 0);
    return currentDate < contestStart || currentDate > contestEnd;
  };
  getDisabledPrizeEndDate = (current: Date): boolean => {
    const contestEndRaw = this.addContentForm.get('end_date')?.value;
    const prizeStartRaw = this.addContentForm.get('prize_start_date')?.value;
    if (!contestEndRaw || !prizeStartRaw) return true;
    const contestEnd = new Date(contestEndRaw);
    const prizeStart = new Date(prizeStartRaw);
    const currentDate = new Date(current);
    // Normalize to midnight
    contestEnd.setHours(0, 0, 0, 0);
    prizeStart.setHours(0, 0, 0, 0);
    currentDate.setHours(0, 0, 0, 0);
    // ❗ Allow same-day selection too
    return currentDate < prizeStart || currentDate > contestEnd;
  };



  initModalForm(): void {
    this.modalForm = this.fb.group({
      start_date: [''],
      end_date: [''],
    });
    this.modalForm.get('start_date')?.valueChanges.subscribe((value) => {
      this.updateEndDateState(value);
    });
  }
  showModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    if (this.modalForm.valid) {
      if (!this.lastPrizeGroup) {
        const prizeGroup = this.createPrize();
        prizeGroup.patchValue({
          start_date: this.modalForm.get('start_date')?.value,
          end_date: this.modalForm.get('end_date')?.value,
        });
        this.prizeArray.push(prizeGroup);
      } else {
        this.lastPrizeGroup.patchValue({
          start_date: this.modalForm.get('start_date')?.value,
          end_date: this.modalForm.get('end_date')?.value,
        });
      }
      this.isVisible = false;
    } else {
      this.modalForm.markAllAsTouched();
    }
  }
  handleCancel(): void {
    this.isVisible = false;
  }
  fileData: { [key: string]: File | string } = {}; // Store files separately
  get prizeArray(): FormArray {
    return this.addContentForm.get('_contest_prize') as FormArray;
  }
  createPrize(): FormGroup {
    return this.fb.group({
      name: ['', Validators.required],
      value: ['', [Validators.required, Validators.min(0)]],
      image_code: ['', Validators.required],
      category_type: ['', Validators.required],
      id: [null],
    });
  }
  timelineOrDateValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const timeline = group.get('timeline')?.value;
    const startDate = group.get('start_date')?.value;
    const endDate = group.get('end_date')?.value;
    if (timeline === 0) {
      const startDateValid = !!startDate;
      const endDateValid = !!endDate;
      return startDateValid && endDateValid ? null : { dateRequired: true };
    } else {
      return timeline ? null : { timelineRequired: true };
    }
  };
  get lastPrizeGroup(): FormGroup {
    return this.prizeArray.at(this.prizeArray.length - 1) as FormGroup;
  }
  showModalsubmit(): void {
    this.isVisiblesubmit = true;
  }
  handleOksubmit(): void {
    this.isVisiblesubmit = false;
  }
  handleCancelsubmit(): void {
    this.isVisiblesubmit = false;
  }
  handleAccessCodeModalOk() {
    this.isAccessCodeModalVisible = false;
    this.markFormAsSaved();
    // Navigate to success page after access code modal
    this.router.navigate(['home/contest-submission-success'], {
      queryParams: { mode: this.isEditMode ? 'edit' : 'new', source: 'add-contest', contestId: this.contestID }
    });
  }
  copyAccessCode() {
    if (this.accessCode) {
      navigator.clipboard.writeText(this.accessCode).then(() => {
        this.isCodeCopied = true;
        setTimeout(() => {
          this.isCodeCopied = false;
        }, 2000);
        this.sharedService.createNotification(
          'success',
          'Access code copied to clipboard!'
        );
      });
    }
  }
  // Updated onSubmit method to only validate when NOT saving as draft
  onSubmit(saveAsDraft: boolean) {
    this.sharedService.showLoader();
    if (!saveAsDraft) {
      this.addContentForm.markAllAsTouched();
    }
    if (!this.addContentForm.valid && !saveAsDraft) {
      this.sharedService.hideLoader();
      // Log invalid fields for debugging
      console.log('Invalid form fields:');
      Object.keys(this.addContentForm.controls).forEach(key => {
        const control = this.addContentForm.get(key);
        if (control?.invalid) {
          console.log(`- ${key}:`, control.errors);
          // Check nested controls in FormArrays
          if (key === 'levels_of_contest') {
            this.levels.controls.forEach((level, index) => {
              const levelGroup = level as FormGroup;
              if (levelGroup.invalid) {
                console.log(`  Level ${index} invalid fields:`);
                Object.keys(levelGroup.controls).forEach(field => {
                  const fieldControl = levelGroup.get(field);
                  if (fieldControl?.invalid) {
                    console.log(`    - ${field}:`, fieldControl.errors, 'value:', fieldControl.value);
                  }
                });
              }
            });
          }
        }
      });
      this.sharedService.createNotification(
        'error',
        'Form is invalid! Please check all fields.'
      );
      this.prizes.controls.forEach((prize) =>
        (prize as FormGroup).markAllAsTouched()
      );
      this.levels.controls.forEach((level) =>
        (level as FormGroup).markAllAsTouched()
      );
      return;
    }
    const form = this.addContentForm;
    const level_data = this.levels.controls.map((group: AbstractControl) => {
      const levelType = group.get('type')?.value;
      const levelPayload: any = {
        type: levelType || null,
        description: group.get('description')?.value || '',
        arabic_name: group.get('arabic_name')?.value || '',
        arabic_description: group.get('arabic_description')?.value || '',
        name: group.get('name')?.value || '',
        client_webhook_url: group.get('client_webhook_url')?.value || '',
        client_variables: group.get('client_variables')?.value || [],
        luqta_webhook_url: group.get('luqta_webhook_url')?.value || '',
        start_date: group.get('level_start_date')?.value || null,
        level_start_date: group.get('level_start_date')?.value || null,
        level_timeline: group.get('level_timeline')?.value || null,
        id: group.get('id')?.value || null,
        accept_any_answer: !!group.get('accept_any_answer')?.value,
      };

      if (levelType === 'text') {
        const textContentArray = (group.get('textContent') as FormArray)?.value || [];
        levelPayload.textContent = textContentArray.filter((text: string) => text.trim() !== '').join(' || ');
      } else if (levelType === 'qr') {
        levelPayload.qrData = group.get('qrData')?.value || '';
        levelPayload.textContent = group.get('qrData')?.value || '';
      } else if (levelType === 'image') {
        const levelIndex = this.levels.controls.indexOf(group);
        // Check form value first, then fallback to preview
        let imageValue = group.get('image')?.value || this.levelImagePreviews[levelIndex] || '';

        // Backend expects image object with path field
        if (typeof imageValue === 'string' && imageValue.startsWith('data:image')) {
          levelPayload.image = { path: imageValue };
        } else if (typeof imageValue === 'object' && imageValue?.path) {
          levelPayload.image = imageValue;
        } else {
          levelPayload.image = null;
        }
      }

      if (levelType === 'luqta_weebhook') {
        levelPayload.luqta_webhook_url =
          'https://xi5f-5y62-kbyr.n7e.xano.io/api:BB6HooOR/luqtaWebhook';
      }

      if (levelType === 'quiz') {
        levelPayload.quizzes_id = group.get('quizzes_id')?.value || null;
      }

      return levelPayload;
    });

    const prize_data = this.prizes.controls.map((group: AbstractControl) => {
      const imageCode = group.get('image_code')?.value || '';
      return {
        name: group.get('name')?.value || '',
        value: group.get('value')?.value || 0,
        image: {
          name: form.get('name')?.value || '',
          type: form.get('type')?.value || '',
          size: 0,
          meta: 0,
          mime: 0,
          path: imageCode,
          url: imageCode,
        },
        categories: this.extractCategoryIds(group.get('category_type')?.value),
        id: group.get('id')?.value || null,
      };
    });

    // ✅ FIX: Get type value properly - prioritize existing data in edit mode
    const contestType = form.get('type')?.value ||
      (this.isEditMode && this.singleContestData?.type) ||
      null;

    // ✅ VALIDATION: Type is required for final submission (not draft)
    if (!contestType && !saveAsDraft) {
      this.sharedService.hideLoader();
      this.sharedService.createNotification(
        'error',
        'Contest type is required for submission.'
      );
      return;
    }

    // ✅ BASE PAYLOAD - NO STATUS FIELD
    const basePayload: any = {
      name: form.get('name')?.value || '',
      arabic_name: form.get('arabic_name')?.value || '',
      details: form.get('details')?.value || '',
      arabic_details: form.get('arabic_details')?.value || '',
      gender: form.get('gender')?.value || 'both',
      age: Array.isArray(form.get('age')?.value)
        ? form.get('age')?.value
        : [form.get('age')?.value || 'All'],
      location: Array.isArray(this.addContentForm.get('location')?.value)
        ? this.addContentForm.get('location')?.value.join(',')
        : this.addContentForm.get('location')?.value || '',
      number_of_winners: form.get('number_of_winners')?.value || 0,
      type: contestType,
      sharingSetting: form.get('sharingSetting')?.value || '',
      prize_announce_date: form.get('prize_announce_date')?.value
        ? new Date(form.get('prize_announce_date')?.value).toISOString()
        : null,
      start_date: form.get('start_date')?.value
        ? new Date(form.get('start_date')?.value).toISOString()
        : null,
      end_date: form.get('end_date')?.value
        ? new Date(form.get('end_date')?.value).toISOString()
        : null,
      format_of_contest: form.get('format_of_contest')?.value || '',
      totallevels: form.get('totallevels')?.value || 0,
      banners: (form.get('banners_code') as FormArray).value.map(
        (base64: string) => ({
          name: form.get('name')?.value || '',
          type: form.get('type')?.value || '',
          size: 0,
          meta: 0,
          mime: 0,
          path: base64,
          url: base64,
        })
      ),
      _contest_prize: prize_data,
      _levels_of_contest: level_data
    };

    // ✅ Status handling
    if (saveAsDraft) {
      basePayload.status = 'draft'; // Only when Save as Draft is clicked
    } else if (this.isEditMode && this.singleContestData?.status === 'draft') {
      basePayload.status = 'pending'; // If editing a draft and clicking Submit
    }
    if (!this.isEditMode) {
      // CREATE NEW CONTEST
      this.pagesService.update('REQUEST_CONTEST', basePayload)
        .pipe(
          timeout(15000),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: 'Request timed out. Please try again.' } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: async (response: any) => {
            try {
              // Save prizes and levels
              await this.setPrizeAsync(response.id, prize_data);
              await this.setLevelAsync(response.id, level_data);

              this.sharedService.hideLoader();
              this.dataService.refreshContestData();
              this.markFormAsSaved();

              if (saveAsDraft) {
                this.sharedService.createNotification('success', 'Draft saved successfully');
                this.router.navigate(['home/contests']);
              } else {
                this.contestID = response.id;
                if (response.type === 'private' && response.access_code) {
                  this.accessCode = response.access_code;
                  this.isAccessCodeModalVisible = true;
                } else {
                  this.router.navigate(['home/contest-submission-success'], {
                    queryParams: { mode: 'new', source: 'add-contest', contestId: response.id }
                  });
                }
              }
              this.addContentForm.reset();
              (this.addContentForm.get('banners_code') as FormArray).clear();
              this.prizes.clear();
              this.levels.clear();
              this.prizeCategoryOptions = [];
              this.prizeArray.push(this.createPrize());
              this.prizeCategoryOptions.push(this.cloneCategoriesTree());
              this.imagePreview = [];
            } catch (error: any) {
              this.sharedService.hideLoader();
              this.sharedService.createNotification('error', error?.error?.message || 'Failed to save contest data');
            }
          },
          error: (err) => {
            this.sharedService.hideLoader();
            this.sharedService.createNotification('error', err.error?.message || 'Failed to save contest');
          },
        });
    } else {
      // UPDATE EXISTING CONTEST
      if (!this.contestID) {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          'Contest ID is missing for update!'
        );
        return;
      }
      const updatedPayload = {
        ...this.singleContestData,
        ...basePayload,
        prize_announce_date: basePayload.prize_announce_date
          ? basePayload.prize_announce_date
          : this.singleContestData.prize_announce_date,
        banners:
          basePayload.banners && basePayload.banners.length > 0
            ? basePayload.banners
            : this.singleContestData.banners || [],
        contest_id: this.contestID,
        user_id: this.authService.getUserId() || this.singleContestData.user_id,
        _contest_prize: prize_data,
        _levels_of_contest: level_data,
      };

      this.pagesService
        .patch('EDIT_CONTEST_CLIENT', this.contestID, updatedPayload)
        .pipe(
          timeout(15000),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: 'Request timed out. Please try again.' } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: async (response: any) => {
            try {
              // Save prizes and levels
              await this.setPrizeAsync(this.contestID, prize_data);
              await this.setLevelAsync(this.contestID, level_data);

              this.sharedService.hideLoader();
              this.dataService.refreshContestData();
              this.markFormAsSaved();

              if (saveAsDraft) {
                this.sharedService.createNotification('success', 'Draft updated successfully');
                this.router.navigate(['home/contests']);
              } else {
                this.router.navigate(['home/contest-submission-success'], {
                  queryParams: { mode: 'edit', source: 'add-contest', contestId: this.contestID }
                });
              }
            } catch (error: any) {
              this.sharedService.hideLoader();
              this.sharedService.createNotification('error', error?.error?.message || 'Failed to save contest data');
            }
          },
          error: (err) => {
            this.sharedService.hideLoader();
            this.sharedService.createNotification('error', err.error?.message || 'Failed to update contest');
          },
        });
    }
  }
  // Async version that returns a promise
  async setPrizeAsync(id: string | null, data?: any[]): Promise<void> {
    return new Promise((resolve, reject) => {
      let prizePayload: any[] = [];
      if (data && data.length > 0) {
        prizePayload = data.map((item, index) => ({
          name: item.name,
          value: item.value,
          image: item.image,
          categories: item.categories || null,
          id: item.id || null,
        }));
      }
      const prizeData = {
        _contest_prize: prizePayload,
        contest_id: id,
      };
      this.authService.login('REQUEST_CONTEST_SET_PRIZE', prizeData)
        .pipe(
          timeout(15000),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: 'Request timed out. Please try again.' } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: () => resolve(),
          error: (error) => {
            this.sharedService.createNotification('error', error.error?.message || 'Failed to save prizes');
            reject(error);
          },
        });
    });
  }

  // Async version that returns a promise
  async setLevelAsync(id: string | null, data?: any[]): Promise<void> {
    return new Promise(async (resolve, reject) => {
      let levelsData: any;
      if (data && data?.length > 0) {
        levelsData = {
          level_details: data,
          contest_id: id,
        };
      } else {
        const level_details = await this.getLevelsData();
        levelsData = {
          level_details,
          contest_id: id,
        };
      }
      this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelsData)
        .pipe(
          timeout(15000),
          catchError(err => {
            if (err.name === 'TimeoutError') {
              return throwError(() => ({ error: { message: 'Request timed out. Please try again.' } }));
            }
            return throwError(() => err);
          })
        )
        .subscribe({
          next: () => resolve(),
          error: (error) => {
            this.sharedService.createNotification('error', error.error?.message || 'Failed to save levels');
            reject(error);
          },
        });
    });
  }

  async setLevel(id: string | null, data?: any[]) {
    this.sharedService.showLoader();
    let levelsData: any;
    if (data && data?.length > 0) {
      levelsData = {
        level_details: data,
        contest_id: id,
      };
    } else {
      const level_details = await this.getLevelsData();
      levelsData = {
        level_details,
        contest_id: id,
      };
    }
    this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelsData).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        // this.router.navigate(['home/contests']);
      },
      error: (error) => {
        this.sharedService.createNotification('error', error.error.message);
        this.sharedService.hideLoader();
      },
    });
  }
  setPrize(id: string | null, data?: any[]) {
    this.sharedService.showLoader();
    let prizePayload: any[] = [];
    if (data && data.length > 0) {
      // Use passed-in data directly (from onSubmit)
      prizePayload = data.map((item, index) => ({
        name: item.name,
        value: item.value,
        image: item.image,
        categories: item.categories || null,
        id: item.id || null,
      }));
    } else {
      // Fallback to form data if not passed
      const prizesArray = this.addContentForm.get(
        '_contest_prize'
      ) as FormArray;
      prizePayload = prizesArray.controls.map((group) => ({
        name: group.get('name')?.value,
        value: group.get('value')?.value,
        image: {
          name: this.addContentForm.get('name')?.value,
          type: this.addContentForm.get('type')?.value,
          size: 0,
          meta: 0,
          mime: 0,
          path: group.get('image_code')?.value,
          url: group.get('image_code')?.value,
        },
        categories: this.extractCategoryIds(group.get('category_type')?.value),
        id: null,
      }));
    }
    const prizeData = {
      _contest_prize: prizePayload,
      contest_id: id,
    };
    this.authService.login('REQUEST_CONTEST_SET_PRIZE', prizeData).subscribe({
      next: () => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'success',
          'Prizes saved successfully'
        );
      },
      error: (error) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          error.error?.message || 'Failed to save prizes'
        );
      },
    });
  }
  triggerProfileFileInput(): void {
    this.profileFileInput.nativeElement.click();
  }
  triggerPrizeFileInput(index: number) {
    const fileInput = document.getElementById(
      `prizeFileInput${index}`
    ) as HTMLInputElement;
    fileInput?.click();
  }
  onBannerFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    const bannersArray = this.addContentForm.get('banners_code') as FormArray;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result as string;
          this.bannerPreviews.push(base64Data);
          bannersArray.push(this.fb.control(base64Data));
        };
        reader.readAsDataURL(file);
      });
    }
    input.value = '';
  }
  // Handling prize image selection
  onPrizeFileSelected(event: Event, index: number) {
    this.handleFileSelection(event, 'image_code', index);
  }
  handleFileSelection(event: any, field: string, index: number) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (!Array.isArray(this.imagePreview)) {
          this.imagePreview = [];
        }
        const base64 = reader.result as string;
        this.imagePreview[index] = { [field]: base64 };
        const control = this.prizeArray.at(index).get(field);
        control?.setValue(base64);
        control?.markAsTouched();
        control?.updateValueAndValidity();
      };
      reader.readAsDataURL(file);
    }
  }
  prevBanner(event: Event): void {
    event.stopPropagation();
    if (this.activeBannerIndex > 0) {
      this.activeBannerIndex--;
    }
  }

  nextBanner(event: Event): void {
    event.stopPropagation();
    if (this.activeBannerIndex < this.bannerPreviews.length - 1) {
      this.activeBannerIndex++;
    }
  }

  goToBanner(index: number, event: Event): void {
    event.stopPropagation();
    this.activeBannerIndex = index;
  }

  // For removing uploaded banner
  removeBanner(index: number): void {
    // Remove from preview array
    this.bannerPreviews.splice(index, 1);

    // Remove from FormArray
    const bannersArray = this.addContentForm.get('banners_code') as FormArray;
    if (index < bannersArray.length) {
      bannersArray.removeAt(index);
    }

    // ✅ Update singleContestData if in edit mode
    if (this.isEditMode && this.singleContestData?.banners) {
      this.singleContestData.banners.splice(index, 1);
    }

    // Reset active index if needed
    if (this.activeBannerIndex >= this.bannerPreviews.length) {
      this.activeBannerIndex = Math.max(0, this.bannerPreviews.length - 1);
    }
  }
  get levels() {
    return this.addContentForm.get('levels_of_contest') as FormArray;
  }
  private updateLevels(count: number) {
    const currentLength = this.levels.length;
    // If increasing, add new groups; if decreasing, remove extras
    if (count > currentLength) {
      for (let i = currentLength; i < count; i++) {
        const newLevelGroup = this.fb.group({
          id: [null],
          type: [null, Validators.required],
          name: ['', Validators.required],
          arabic_name: ['', Validators.required],
          description: ['', [Validators.required, Validators.maxLength(1000)]],
          arabic_description: ['', [Validators.required, Validators.maxLength(1000)]],
          level_start_date: [null],
          level_timeline: [null, Validators.required],
          textContent: this.fb.array([this.fb.control('', [Validators.maxLength(1000)])]),
          imageFile: [null],
          qrData: [''],
          client_webhook_url: [''],
          luqta_webhook_url: [''],
          client_variables: this.fb.array([]),
          quizzes_id: [null],
        }, { validators: this.levelDateOrderValidator });

        this.subscribeToLevelChanges(newLevelGroup, i);
        this.levels.push(newLevelGroup);
        this.levelQrCodes.push(null);
        this.levelImagePreviews.push(null);
        this.isImageVisible.push(false);
      }
    } else if (count < currentLength) {
      for (let i = currentLength - 1; i >= count; i--) {
        this.levels.removeAt(i);
        this.levelQrCodes.splice(i, 1);
        this.levelImagePreviews.splice(i, 1);
        this.isImageVisible.splice(i, 1);
      }
      // this.calculateTotalPoints();
    }
    // If count == currentLength, do nothing (keep existing data)
  }

  subItems(index: number): FormArray {
    const group = this.levels.at(index) as FormGroup;
    return (group.get('client_variables') as FormArray) ?? this.fb.array([]);
  }
  deleteSubItem(levelIndex: number, subIndex: number) {
    const clientVariables = this.subItems(levelIndex);
    if (clientVariables && clientVariables.length > subIndex) {
      clientVariables.removeAt(subIndex);
      (this.levels.at(levelIndex) as FormGroup).updateValueAndValidity();
    }
  }
  // add subItem to a given item
  addSubItem(itemIndex: number) {
    this.subItems(itemIndex).push(
      this.fb.group({
        variable_name: ['', [Validators.required]],
        display_name: ['', [Validators.required]],
        datatype: ['', [Validators.required]],
      })
    );
    (this.levels.at(itemIndex) as FormGroup).updateValueAndValidity();
  }

  private createClientVariableGroup(): FormGroup {
    return this.fb.group({
      variable_name: ['', [Validators.required]],
      display_name: ['', [Validators.required]],
      datatype: ['', [Validators.required]],
    });
  }

  ngOnDestroy(): void {
    this.stopContestCountdown();
  }

  // Get textContent FormArray for a specific level
  getTextContents(levelIndex: number): FormArray {
    const group = this.levels.at(levelIndex) as FormGroup;
    return (group.get('textContent') as FormArray) ?? this.fb.array([]);
  }

  getAcceptAnyAnswerControl(levelIndex: number): FormControl {
    return (this.levels.at(levelIndex) as FormGroup).get('accept_any_answer') as FormControl;
  }

  // Add a new text content field
  addTextContent(levelIndex: number) {
    const textContents = this.getTextContents(levelIndex);
    textContents.push(this.fb.control('', [Validators.required, Validators.maxLength(1000)]));
  }

  // Remove a text content field
  removeTextContent(levelIndex: number, textIndex: number) {
    const textContents = this.getTextContents(levelIndex);
    if (textContents && textContents.length > 1) {
      textContents.removeAt(textIndex);
    }
  }
  subscribeToLevelTypeChanges(levelGroup: FormGroup, index: number): void {
    levelGroup.get('type')?.valueChanges.subscribe((type) => {
      const textContentArray = levelGroup.get('textContent') as FormArray;
      const imageFileControl = levelGroup.get('imageFile');
      const qrDataControl = levelGroup.get('qrData');
      const clientWebhookControl = levelGroup.get('client_webhook_url');
      const luqtaWebhookControl = levelGroup.get('luqta_webhook_url');
      const quizzesIdControl = levelGroup.get('quizzes_id');

      // Reset ALL conditional fields and clear validators when type changes
      // Clear textContent array and reset with one empty field
      textContentArray?.clear();
      if (type === 'text') {
        textContentArray?.push(this.fb.control('', [Validators.required, Validators.maxLength(1000)]));
      } else {
        textContentArray?.push(this.fb.control(''));
      }

      imageFileControl?.clearValidators();
      imageFileControl?.setValue(null, { emitEvent: false });
      this.levelImagePreviews[index] = null;

      qrDataControl?.clearValidators();
      qrDataControl?.setValue('', { emitEvent: false });
      this.levelQrCodes[index] = null;

      clientWebhookControl?.clearValidators();
      clientWebhookControl?.setValue('', { emitEvent: false });

      // Set luqta_webhook_url only when type is luqta_weebhook
      if (type === 'luqta_weebhook') {
        luqtaWebhookControl?.setValue('https://xi5f-5y62-kbyr.n7e.xano.io/api:BB6HooOR/luqtaWebhook', { emitEvent: false });
      } else {
        luqtaWebhookControl?.setValue('', { emitEvent: false });
      }

      quizzesIdControl?.clearValidators();
      quizzesIdControl?.setValue(null, { emitEvent: false });

      // Set validators based on selected type
      if (type === 'qr') {
        qrDataControl?.setValidators([Validators.required]);
      } else if (type === 'image') {
        imageFileControl?.setValidators([Validators.required]);
      } else if (type === 'client_webhook') {
        clientWebhookControl?.setValidators([
          Validators.required,
          Validators.pattern(/^https?:\/\/.+/)
        ]);
      } else if (type === 'quiz') {
        quizzesIdControl?.setValidators([Validators.required]);
      }

      // Client Webhook should always start with one variable row by default.
      if (type === 'client_webhook') {
        const variables = levelGroup.get('client_variables') as FormArray;
        if (variables && variables.length === 0) {
          variables.push(this.createClientVariableGroup());
        }
      }

      // Update validity for all controls
      textContentArray?.updateValueAndValidity({ emitEvent: false });
      imageFileControl?.updateValueAndValidity({ emitEvent: false });
      qrDataControl?.updateValueAndValidity({ emitEvent: false });
      clientWebhookControl?.updateValueAndValidity({ emitEvent: false });
      luqtaWebhookControl?.updateValueAndValidity({ emitEvent: false });
      quizzesIdControl?.updateValueAndValidity({ emitEvent: false });
      levelGroup.updateValueAndValidity({ emitEvent: false });
    });
  }
  generateQrCode(index: number): void {
    const levelGroup = this.levels.at(index) as FormGroup;
    const qrData = levelGroup.get('qrData')?.value;
    if (qrData) {
      this.levelQrCodes[index] = qrData;
      this.levelQrCodesOriginal[index] = qrData; // Store original value
      this.showQRModal();
    } else {
      this.levelQrCodes[index] = null;
      this.sharedService.createNotification(
        'error',
        'Please enter text for QR code generation.'
      );
    }
  }

  // Check if QR input has changed since last generation
  hasQrInputChanged(index: number): boolean {
    const levelGroup = this.levels.at(index) as FormGroup;
    const currentQrData = levelGroup.get('qrData')?.value;
    return currentQrData !== this.levelQrCodesOriginal[index];
  }

  // Check if generate button should be disabled
  isQrGenerateDisabled(index: number): boolean {
    const levelGroup = this.levels.at(index) as FormGroup;
    const qrData = levelGroup.get('qrData')?.value;
    return !qrData || qrData.trim() === '';
  }
  triggerImageUpload(imageInput: HTMLInputElement): void {
    imageInput.click();
  }

  // Trigger level image file input
  triggerLevelImageInput(index: number): void {
    // If image already exists in preview, patch it to the form first
    if (this.levelImagePreviews[index]) {
      const levelGroup = this.levels.at(index) as FormGroup;
      const existingImage = levelGroup.get('image')?.value;

      // If form doesn't have image value but preview exists, set it
      if (!existingImage && this.levelImagePreviews[index]) {
        levelGroup.get('image')?.setValue(this.levelImagePreviews[index]);
      }
    }

    // Now trigger file input for new upload
    const fileInput: any = document.getElementById(`imageFile_${index}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Show image preview modal
  showImageModal(index: number): void {
    if (this.levelImagePreviews[index]) {
      this.isImageVisible[index] = true;
    }
  }

  // Handle image modal OK / Save Image
  handleImageOk(index: number): void {
    // Patch image to form when user clicks Save Image button
    if (this.levelImagePreviews[index]) {
      const levelGroup = this.levels.at(index) as FormGroup;
      const imagePreview = this.levelImagePreviews[index];

      // Set image value (base64)
      levelGroup.get('image')?.setValue(imagePreview);

      // Create a dummy file object for validation if it's base64
      if (typeof imagePreview === 'string' && imagePreview.startsWith('data:image')) {
        // Mark imageFile as valid since we have image data
        levelGroup.get('imageFile')?.setValue('has_image'); // Placeholder to pass validation
      }

      levelGroup.get('imageFile')?.markAsTouched();
      levelGroup.get('imageFile')?.updateValueAndValidity();

      this.sharedService.createNotification('success', 'Image saved successfully');
    }
    this.isImageVisible[index] = false;
  }

  // Handle image modal cancel
  handleImageCancel(index: number): void {
    this.isImageVisible[index] = false;
  }

  async onLevelImageSelected(event: Event, index: number) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        // Just update preview, don't patch to form yet
        this.levelImagePreviews[index] = reader.result;

        // Show/refresh modal after image is uploaded
        this.showImageModal(index);
      };
      reader.readAsDataURL(file);
    } else {
      this.levelImagePreviews[index] = null;
    }
  }
  confirmRemoveLevel(index: number): void {
    // Prevent deleting the last remaining level
    if (this.levels.length === 1) {
      this.sharedService.createNotification('warning', 'At least one level is required.');
      return;
    }

    const levelGroup = this.levels.at(index) as FormGroup;
    const hasData = Object.keys(levelGroup.controls).some(
      (key) => levelGroup.get(key)?.value
    );

    if (hasData) {
      this.showConfirm(index);
    } else {
      this.removeLevel(index);
    }
  }

  removeLevel(index: number): void {
    this.levels.removeAt(index);
    this.levelQrCodes.splice(index, 1);
    this.levelImagePreviews.splice(index, 1);
    this.isImageVisible.splice(index, 1);
    const originalData = this.singleContestData || {};
    let levelId: string | null = null;
    if (
      originalData._levels_of_contest &&
      originalData._levels_of_contest[index]
    ) {
      levelId = originalData._levels_of_contest[index].id || null;
      originalData._levels_of_contest.splice(index, 1);
    }
    if (levelId) {
      this.authService
        .deletebyId('REQUEST_CONTEST_REMOVE_LEVEL', levelId)
        .subscribe({
          next: () => {
            this.sharedService.createNotification(
              'success',
              'Level removed successfully'
            );
          },
          error: (error) => {
            this.sharedService.createNotification('error', error.error.message);
          },
        });
    }
    // this.calculateTotalPoints();
    this.addContentForm
      .get('totallevels')
      ?.setValue(this.levels.length || null, { emitEvent: true });
  }
  showConfirm(index: number): void {
    this.modal.confirm({
      nzTitle: 'Confirm Removal',
      nzContent:
        'This level contains data. Are you sure you want to remove it?',
      nzOnOk: () => this.removeLevel(index),
      nzClassName: 'custom-confirm-modal',
      nzCentered: true,
      nzOnCancel: () => { },
    });
  }
  async getLevelsData(): Promise<any[] | null> {
    if (!this.addContentForm.get('levels_of_contest')?.valid) {
      (
        this.addContentForm.get('levels_of_contest') as FormArray
      ).controls.forEach((levelControl) => {
        (levelControl as FormGroup).markAllAsTouched();
      });
      this.sharedService.createNotification(
        'error',
        'Please fill all required level fields.'
      );
      return null;
    }
    const levelsArray = this.addContentForm.get(
      'levels_of_contest'
    ) as FormArray;
    const levelDataPromises = levelsArray.controls.map(async (levelControl) => {
      const levelGroup = levelControl as FormGroup;
      const levelType = levelGroup.get('type')?.value;
      const data: any = {
        type: levelType,
        description: levelGroup.get('description')?.value || '',
        name: levelGroup.get('name')?.value || '',
        arabic_name: levelGroup.get('arabic_name')?.value || '',
        arabic_description: levelGroup.get('arabic_description')?.value || '',
        // points: levelGroup.get('points')?.value || '',
        start_date: levelGroup.get('level_start_date')?.value || null,
        level_start_date: levelGroup.get('level_start_date')?.value || null,
        level_timeline: levelGroup.get('level_timeline')?.value,
      };
      switch (levelType) {
        case 'text':
          const textContentArray = (levelGroup.get('textContent') as FormArray)?.value || [];
          data.textContent = textContentArray.filter((text: string) => text.trim() !== '').join(' || ');
          break;
        case 'qr':
          data.qrData = levelGroup.get('qrData')?.value;
          data.textContent = levelGroup.get('qrData')?.value;
          break;
        case 'image':
          const imageFile = levelGroup.get('image')?.value;
          if (
            typeof imageFile === 'string' &&
            imageFile.startsWith('data:image')
          ) {
            data.image = imageFile;
          } else if (imageFile instanceof File) {
            data.image = await this.fileToBase64(imageFile);
          } else {
            data.image = '';
          }
          break;
        case 'client_webhook':
          data.client_webhook_url = levelGroup.get('client_webhook_url')?.value;
          data.client_variables = levelGroup.get('client_variables')?.value;
          break;
        case 'luqta_weebhook':
          data.luqta_webhook_url =
            'https://xi5f-5y62-kbyr.n7e.xano.io/api:BB6HooOR/luqtaWebhook';
          break;
        case 'quiz':
          data.quizzes_id = levelGroup.get('quizzes_id')?.value || null;
          break;
      }
      return data;
    });
    return Promise.all(levelDataPromises);
  }
  setLevelsData(levelsData: any[]) {
    this.levels.clear();
    this.isImageVisible = [];
    levelsData.forEach((level: any) => {
      const clientVariablesArray = this.fb.array(
        (level?.client_variables || []).map((cv: any) =>
          this.fb.group({
            variable_name: [cv.variable_name || '', Validators.required],
            display_name: [cv.display_name || '', Validators.required],
            datatype: [cv.datatype || '', Validators.required],
          })
        )
      );
      const levelGroup = this.fb.group({
        id: [level?.id],
        type: [level?.level_type || level?.type, Validators.required], // Accommodate existing 'type' or 'level_type'
        description: [
          level?.description,
          [Validators.required, Validators.maxLength(1000)],
        ],
        name: [level?.name, Validators.required],
        arabic_name: [level?.arabic_name, Validators.required],
        arabic_description: [level?.arabic_description, Validators.required],
        luqta_webhook_url: [level?.luqta_webhook_url],
        client_webhook_url: [level?.client_webhook_url],
        client_variables: clientVariablesArray,
        level_start_date: [level?.level_start_date ? new Date(level.level_start_date) : (level?.start_date ? new Date(level.start_date) : null)],
        level_timeline: [level?.level_timeline ? new Date(level.level_timeline) : null, Validators.required],
        accept_any_answer: [level?.accept_any_answer ?? false],
        textContent: this.fb.array(
          level?.textContent && level.textContent.trim() !== ''
            ? level.textContent.split(' || ').map((text: string) =>
              this.fb.control(text, (level?.level_type || level?.type) === 'text' ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])
            )
            : [this.fb.control('', (level?.level_type || level?.type) === 'text' ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])]
        ),
        image: [level?.image || null],
        imageFile: [null],
        qrData: [level?.qrData || level?.textContent],
        quizzes_id: [level?.quizzes_id || null],
      }, { validators: this.levelDateOrderValidator });
      if (
        levelGroup.get('type')?.value === 'qr' &&
        levelGroup.get('qrData')?.value
      ) {
        this.levelQrCodes[this.levels.length] = levelGroup.get('qrData')?.value;
      } else {
        this.levelQrCodes[this.levels.length] = null;
      }
      if (levelGroup.get('type')?.value === 'image' && level.image) {
        // Set image preview for AI-generated or existing image
        const imageUrl = level.image.url || level.image.path || level.image;
        this.levelImagePreviews[this.levels.length] = imageUrl;
        console.log(`Set image preview for level ${this.levels.length}:`, imageUrl ? imageUrl.substring(0, 50) + '...' : 'none');

        // Clear validators since image is already provided
        levelGroup.get('imageFile')?.clearValidators();
        levelGroup.get('imageFile')?.updateValueAndValidity();
      } else if (levelGroup.get('type')?.value === 'quiz') {
        if (!this.quizzesLoaded) {
          this.getQuizzesRecords();
        }
        levelGroup.get('quizzes_id')?.setValidators([Validators.required]);
        levelGroup.get('quizzes_id')?.updateValueAndValidity();
      } else {
        this.levelImagePreviews[this.levels.length] = null;
      }
      this.subscribeToLevelChanges(levelGroup, this.levels.length);
      this.levels.push(levelGroup);
      if (levelGroup.get('type')?.value === 'client_webhook') {
        const variables = levelGroup.get('client_variables') as FormArray;
        if (variables.length === 0) {
          variables.push(this.createClientVariableGroup());
          levelGroup.updateValueAndValidity({ emitEvent: false });
        }
      }
      this.isImageVisible.push(false);
    });
  }
  get prizes(): FormArray {
    return this.addContentForm.get('_contest_prize') as FormArray;
  }
  async setPrizesData(prizesData: any[]) {
    this.prizes.clear();
    this.prizeCategoryOptions = [];
    if (!this.categoriesLoaded) {
      await this.loadCategoriesAsync();
    }
    for (const prize of prizesData) {
      const cascaderValue = this.categoryIdsToCascaderValue(prize?.categories);
      const prizeGroup = this.fb.group({
        name: [prize?.name, Validators.required],
        value: [prize?.value, [Validators.required, Validators.min(0)]],
        image_code: [prize?.image?.path, Validators.required],
        category_type: [cascaderValue, Validators.required],
        id: [prize?.id],
      });
      this.prizes.push(prizeGroup);
      this.prizeCategoryOptions.push(this.cloneCategoriesTree());
      const index = this.prizes.length - 1;
      if (!Array.isArray(this.imagePreview)) {
        this.imagePreview = [];
      }
      let prizeImageUrl = prize?.image?.url || prize?.image?.path || '';
      if (prizeImageUrl.includes('https://xi5f-5y62-kbyr.n7e.xano.iohttps://')) {
        prizeImageUrl = prizeImageUrl.replace(
          'https://xi5f-5y62-kbyr.n7e.xano.iohttps://',
          'https://'
        );
      }
      this.imagePreview[index] = {
        image_code: prizeImageUrl,
      };
    }
  }
  async getContestDetails(id: string) {
    this.sharedService.showLoader();
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getById('GET_SINGLE_CONTEST', id)
      );
      if (response) {
        this.singleContestData = Array.isArray(response)
          ? response[0]
          : response;

        const ageValue = this.singleContestData.age;
        const ageArray = Array.isArray(ageValue) ? ageValue : [ageValue];

        const startDate = this.singleContestData.start_date
          ? new Date(this.singleContestData.start_date)
          : null;

        const endDate = this.singleContestData.end_date
          ? new Date(this.singleContestData.end_date)
          : null;

        const prizeAnnounceDate = this.singleContestData.prize_announce_date
          ? new Date(this.singleContestData.prize_announce_date)
          : null;

        this.addContentForm.patchValue({
          name: this.singleContestData.name,
          arabic_name: this.singleContestData.arabic_name,
          details: this.singleContestData.details,
          arabic_details: this.singleContestData.arabic_details,
          gender: this.singleContestData.gender,
          age: ageArray,
          location: this.singleContestData.location
            ? this.singleContestData.location.split(',')
            : [],
          number_of_winners: JSON.stringify(this.singleContestData.number_of_winners),
          type: this.singleContestData.type,
          sharingSetting: this.singleContestData.sharingSetting,
          format_of_contest: this.singleContestData.format_of_contest,
          totallevels: this.singleContestData._levels_of_contest?.length || 0
        });

        this.addContentForm.patchValue({
          start_date: startDate,
          end_date: endDate,
        }, { emitEvent: false });

        setTimeout(() => {
          this.addContentForm.patchValue({
            prize_announce_date: prizeAnnounceDate
          });
        }, 0);

        // Rest of your code...
        this.bannerPreviews = [];
        const bannersArray = this.addContentForm.get('banners_code') as FormArray;
        bannersArray.clear();
        if (this.singleContestData.banners && Array.isArray(this.singleContestData.banners)) {
          this.singleContestData.banners.forEach((banner: any) => {
            let imageUrl = banner.url || banner.path || '';

            if (imageUrl.includes('https://xi5f-5y62-kbyr.n7e.xano.iohttps://')) {
              imageUrl = imageUrl.replace(
                'https://xi5f-5y62-kbyr.n7e.xano.iohttps://',
                'https://'
              );
            }

            this.bannerPreviews.push(imageUrl);
            bannersArray.push(this.fb.control(imageUrl));
          });
        }

        this.imagePreview = [];
        if (Array.isArray(this.singleContestData._contest_prize)) {
          this.singleContestData._contest_prize.forEach(
            (prize: any, index: number) => {
              let prizeImageUrl = prize?.image?.url || prize?.image?.path || '';

              if (prizeImageUrl.includes('https://xi5f-5y62-kbyr.n7e.xano.iohttps://')) {
                prizeImageUrl = prizeImageUrl.replace(
                  'https://xi5f-5y62-kbyr.n7e.xano.iohttps://',
                  'https://'
                );
              }

              this.imagePreview[index] = {
                image_code: prizeImageUrl,
              };
            }
          );
        }

        // ✅ Sort levels by their timeline or id to maintain order
        if (this.singleContestData._levels_of_contest && Array.isArray(this.singleContestData._levels_of_contest)) {
          const sortedLevels = [...this.singleContestData._levels_of_contest].sort((a, b) => {
            // If there's a sequence/order field in your data, use it
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            // Otherwise, sort by timeline
            if (a.level_timeline && b.level_timeline) {
              return new Date(a.level_timeline).getTime() - new Date(b.level_timeline).getTime();
            }
            // Fallback to id if available
            if (a.id && b.id) {
              return a.id - b.id;
            }
            return 0;
          });
          this.setLevelsData(sortedLevels);
        } else {
          this.setLevelsData([]);
        }

        await this.setPrizesData(this.singleContestData._contest_prize);
      }
    } catch (error) {
      this.sharedService.createNotification(
        'error',
        'Failed to fetch contest details.'
      );
    } finally {
      this.sharedService.hideLoader();
    }
  }
  imgPrizeSrc(index: number): string | undefined {
    return this.imagePreview[index]?.image_code || '';
  }
  convertImageUrlToBase64(imageUrl: string): Promise<string> {
    if (imageUrl === '') {
      return Promise.resolve('');
    } else {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.onload = function () {
          const reader = new FileReader();
          reader.onloadend = function () {
            resolve(reader.result as string);
          };
          reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = function () {
          reject(new Error('Failed to convert image to base64'));
        };
        xhr.open('GET', imageUrl);
        xhr.responseType = 'blob';
        xhr.send();
      });
    }
  }
  addPrize(): void {
    const lastIndex = this.prizeArray.length - 1;
    if (lastIndex >= 0) {
      const lastPrizeGroup = this.prizeArray.at(lastIndex) as FormGroup;
      // Mark all controls in the group as touched, then the group itself
      Object.values(lastPrizeGroup.controls).forEach((control) => {
        control.markAsTouched();
        control.updateValueAndValidity({ emitEvent: false }); // Avoid multiple events if not needed
      });
      lastPrizeGroup.markAsTouched();
      lastPrizeGroup.updateValueAndValidity({ emitEvent: true });
      if (lastPrizeGroup.invalid) {
        this.sharedService.createNotification(
          'error',
          'Please fill in the previous prize details before adding a new one.'
        );
        return;
      }
    }
    this.prizeArray.push(this.createPrize());
    this.prizeCategoryOptions.push(this.cloneCategoriesTree());
    this.imagePreview.push({ image_code: null }); // Initialize empty preview for the new prize
  }
  removePrize(index: number): void {
    if (this.prizeArray.length <= 1) {
      this.sharedService.createNotification(
        'error',
        'At least one prize is required. Cannot remove the last prize.'
      );
      return;
    }
    const prizeGroup = this.prizeArray.at(index) as FormGroup;
    const hasData = Object.keys(prizeGroup.controls).some((key) => {
      const control = prizeGroup.get(key);
      if (!control) return false;
      // Name and image_code are primary data points.
      if (key === 'name' && control.value) return true;
      if (key === 'value' && control.value) return true;
      if (key === 'image_code' && control.value) return true;
      return false;
    });
    if (hasData) {
      this.showPrizeRemoveConfirm(index);
    } else {
      // If no data, remove directly
      this.prizeArray.removeAt(index);
      this.imagePreview.splice(index, 1);
      this.prizeCategoryOptions.splice(index, 1);
    }
  }
  private showPrizeRemoveConfirm(index: number): void {
    this.modal.confirm({
      nzTitle: 'Confirm Removal',
      nzContent:
        'This prize contains data. Are you sure you want to remove it?',
      nzOnOk: () => {
        this.prizeArray.removeAt(index);
        this.imagePreview.splice(index, 1);
        this.prizeCategoryOptions.splice(index, 1);
      },
      nzClassName: 'custom-confirm-modal',
      nzCentered: true,
      nzOnCancel: () => { },
    });
  }
  get banners(): FormArray {
    return this.addContentForm.get('banners_code') as FormArray;
  }
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  showQRModal() {
    this.isQrVisible = true;
  }
  handleQROk(): void {
    this.isQrVisible = false;
  }
  handleQRCancel(): void {
    this.isQrVisible = false;
  }
  onDownloadQR(qrElement: ElementRef | HTMLElement): void {
    const canvas = (qrElement as HTMLElement).querySelector('canvas');
    if (canvas instanceof HTMLCanvasElement) {
      const imgData = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = imgData;
      a.download = `qr-code.png`;
      a.click();
    } else {
    }
  }

  private subscribeToPointsChanges(): void {
    // Subscribe to changes in the levels array
    this.levels.valueChanges.subscribe(() => {
      // this.calculateTotalPoints();
    });
  }

  private getLevelBaseBounds(levelIndex: number, forStartDate: boolean = false): { min: Date | null; max: Date | null } {
    const contestStartDate = this.addContentForm.get('start_date')?.value;
    const contestEndDate = this.addContentForm.get('end_date')?.value;

    if (!contestStartDate || !contestEndDate) {
      return { min: null, max: null };
    }

    if (levelIndex === 0) {
      return { min: new Date(contestStartDate), max: new Date(contestEndDate) };
    }

    if (forStartDate) {
      // For start date: min = previous level's start date (allows same start time)
      const previousLevelStartDate = this.getPreviousLevelStartDate(levelIndex);
      const min = previousLevelStartDate || new Date(contestStartDate);
      return { min, max: new Date(contestEndDate) };
    }

    // For expiry date: min = previous level's expiry date (cannot end before previous ends)
    const previousLevelDate = this.getPreviousLevelDate(levelIndex);
    if (!previousLevelDate) {
      return { min: null, max: null };
    }

    return { min: new Date(previousLevelDate), max: new Date(contestEndDate) };
  }

  getDisabledLevelStartDate = (levelIndex: number) => {
    return (current: Date): boolean => {
      if (!current) return false;

      const { min, max } = this.getLevelBaseBounds(levelIndex, true);
      if (!min || !max) return true;

      const currentDateOnly = new Date(current);
      currentDateOnly.setHours(0, 0, 0, 0);

      const minDateOnly = new Date(min);
      minDateOnly.setHours(0, 0, 0, 0);

      const maxDateOnly = new Date(max);
      maxDateOnly.setHours(0, 0, 0, 0);

      if (currentDateOnly.getTime() < minDateOnly.getTime() || currentDateOnly.getTime() > maxDateOnly.getTime()) {
        return true;
      }

      const levelExpiry = this.levels.at(levelIndex)?.get('level_timeline')?.value;
      if (levelExpiry) {
        const expiryDateOnly = new Date(levelExpiry);
        expiryDateOnly.setHours(0, 0, 0, 0);
        if (currentDateOnly.getTime() > expiryDateOnly.getTime()) {
          return true;
        }
      }

      return false;
    };
  };

  getDisabledLevelStartDateTime = (levelIndex: number) => {
    return (current: Date | Date[]) => {
      const currentDate = Array.isArray(current) ? current[0] : current;
      const { min, max } = this.getLevelBaseBounds(levelIndex, true);

      if (!currentDate || !min || !max) {
        return {
          nzDisabledHours: () => this.range(0, 24),
          nzDisabledMinutes: () => this.range(0, 60),
          nzDisabledSeconds: () => this.range(0, 60)
        };
      }

      const currentDateOnly = new Date(currentDate);
      currentDateOnly.setHours(0, 0, 0, 0);
      const minDateOnly = new Date(min);
      minDateOnly.setHours(0, 0, 0, 0);
      const maxDateOnly = new Date(max);
      maxDateOnly.setHours(0, 0, 0, 0);

      const levelExpiryRaw = this.levels.at(levelIndex)?.get('level_timeline')?.value;
      const levelExpiry = levelExpiryRaw ? new Date(levelExpiryRaw) : null;
      const levelExpiryDateOnly = levelExpiry ? new Date(levelExpiry) : null;
      levelExpiryDateOnly?.setHours(0, 0, 0, 0);

      return {
        nzDisabledHours: () => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime()) {
            disabled.push(...this.range(0, min.getHours()));
          }
          if (currentDateOnly.getTime() === maxDateOnly.getTime()) {
            disabled.push(...this.range(max.getHours() + 1, 24));
          }
          if (levelExpiry && levelExpiryDateOnly && currentDateOnly.getTime() === levelExpiryDateOnly.getTime()) {
            disabled.push(...this.range(levelExpiry.getHours() + 1, 24));
          }

          return [...new Set(disabled)];
        },
        nzDisabledMinutes: (hour: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() && hour === min.getHours()) {
            disabled.push(...this.range(0, min.getMinutes()));
          }
          if (currentDateOnly.getTime() === maxDateOnly.getTime() && hour === max.getHours()) {
            disabled.push(...this.range(max.getMinutes() + 1, 60));
          }
          if (levelExpiry && levelExpiryDateOnly && currentDateOnly.getTime() === levelExpiryDateOnly.getTime() && hour === levelExpiry.getHours()) {
            disabled.push(...this.range(levelExpiry.getMinutes(), 60));
          }

          return [...new Set(disabled)];
        },
        nzDisabledSeconds: (hour: number, minute: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() && hour === min.getHours() && minute === min.getMinutes()) {
            disabled.push(...this.range(0, min.getSeconds()));
          }
          if (currentDateOnly.getTime() === maxDateOnly.getTime() && hour === max.getHours() && minute === max.getMinutes()) {
            disabled.push(...this.range(max.getSeconds() + 1, 60));
          }
          if (levelExpiry && levelExpiryDateOnly &&
            currentDateOnly.getTime() === levelExpiryDateOnly.getTime() &&
            hour === levelExpiry.getHours() &&
            minute === levelExpiry.getMinutes()) {
            disabled.push(...this.range(levelExpiry.getSeconds(), 60));
          }

          return [...new Set(disabled)];
        }
      };
    };
  };

  // Method to get disabled dates for a specific level
  getDisabledLevelDate = (levelIndex: number) => {
    return (current: Date): boolean => {
      if (!current) return false;

      const { min, max } = this.getLevelBaseBounds(levelIndex);
      if (!min || !max) return true;

      const levelStartRaw = this.levels.at(levelIndex)?.get('level_start_date')?.value;
      const levelStart = levelStartRaw ? new Date(levelStartRaw) : null;
      const effectiveMin = levelStart && levelStart.getTime() > min.getTime() ? levelStart : min;

      const currentDateOnly = new Date(current);
      currentDateOnly.setHours(0, 0, 0, 0);

      const minDateOnly = new Date(effectiveMin);
      minDateOnly.setHours(0, 0, 0, 0);

      const maxDateOnly = new Date(max);
      maxDateOnly.setHours(0, 0, 0, 0);

      return currentDateOnly.getTime() < minDateOnly.getTime() ||
        currentDateOnly.getTime() > maxDateOnly.getTime();
    };
  };

  // Method to get disabled times for a specific level timeline
  getDisabledLevelDateTime = (levelIndex: number) => {
    return (current: Date | Date[]) => {
      const currentDate = Array.isArray(current) ? current[0] : current;
      const { min, max } = this.getLevelBaseBounds(levelIndex);

      if (!currentDate || !min || !max) {
        return {
          nzDisabledHours: () => this.range(0, 24),
          nzDisabledMinutes: () => this.range(0, 60),
          nzDisabledSeconds: () => this.range(0, 60)
        };
      }

      const levelStartRaw = this.levels.at(levelIndex)?.get('level_start_date')?.value;
      const levelStart = levelStartRaw ? new Date(levelStartRaw) : null;
      const strictAfterStart = !!levelStart;
      const effectiveMin = levelStart && levelStart.getTime() > min.getTime() ? levelStart : min;

      const currentDateOnly = new Date(currentDate);
      currentDateOnly.setHours(0, 0, 0, 0);
      const minDateOnly = new Date(effectiveMin);
      minDateOnly.setHours(0, 0, 0, 0);
      const endDate = new Date(max);
      const endDateOnly = new Date(endDate);
      endDateOnly.setHours(0, 0, 0, 0);

      return {
        nzDisabledHours: () => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime()) {
            disabled.push(...this.range(0, effectiveMin.getHours()));
          }

          if (currentDateOnly.getTime() === endDateOnly.getTime()) {
            disabled.push(...this.range(endDate.getHours() + 1, 24));
          }

          return disabled;
        },
        nzDisabledMinutes: (hour: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() &&
            hour === effectiveMin.getHours()) {
            disabled.push(...this.range(0, strictAfterStart ? effectiveMin.getMinutes() + 1 : effectiveMin.getMinutes()));
          }

          if (currentDateOnly.getTime() === endDateOnly.getTime() &&
            hour === endDate.getHours()) {
            disabled.push(...this.range(endDate.getMinutes() + 1, 60));
          }

          return disabled;
        },
        nzDisabledSeconds: (hour: number, minute: number) => {
          const disabled: number[] = [];

          if (currentDateOnly.getTime() === minDateOnly.getTime() &&
            hour === effectiveMin.getHours() &&
            minute === effectiveMin.getMinutes()) {
            disabled.push(...this.range(0, strictAfterStart ? effectiveMin.getSeconds() + 1 : effectiveMin.getSeconds()));
          }

          if (currentDateOnly.getTime() === endDateOnly.getTime() &&
            hour === endDate.getHours() &&
            minute === endDate.getMinutes()) {
            disabled.push(...this.range(endDate.getSeconds() + 1, 60));
          }

          return disabled;
        }
      };
    };
  };
  // Helper method to get the expiry date from the previous level
  getPreviousLevelDate(currentLevelIndex: number): Date | null {
    if (currentLevelIndex <= 0) return null;
    const previousLevel = this.levels.at(currentLevelIndex - 1);
    const previousLevelTimeline = previousLevel?.get('level_timeline')?.value;
    return previousLevelTimeline ? new Date(previousLevelTimeline) : null;
  }

  // Helper method to get the start date from the previous level
  getPreviousLevelStartDate(currentLevelIndex: number): Date | null {
    if (currentLevelIndex <= 0) return null;
    const previousLevel = this.levels.at(currentLevelIndex - 1);
    const previousLevelStart = previousLevel?.get('level_start_date')?.value;
    return previousLevelStart ? new Date(previousLevelStart) : null;
  }
  // Helper method to provide user-friendly date hints
  getLevelDateHint(levelIndex: number): string {
    const contestStartDate = this.addContentForm.get('start_date')?.value;
    const contestEndDate = this.addContentForm.get('end_date')?.value;
    if (!contestStartDate || !contestEndDate) {
      return 'Set contest dates first';
    }
    if (levelIndex === 0) {
      return `Between contest start and end dates`;
    }
    const previousLevelDate = this.getPreviousLevelDate(levelIndex);
    if (!previousLevelDate) {
      return `Select Level ${levelIndex} timeline first`;
    }
    return `From Level ${levelIndex} date onwards`;
  }
  // Optional: Add a method to clear all level timelines when contest dates change
  private clearAllLevelTimelines(): void {
    this.levels.controls.forEach((level, index) => {
      level.get('level_timeline')?.setValue(null);
    });
    this.sharedService.createNotification(
      'info',
      'All level timelines have been cleared due to contest date changes.'
    );
  }
  private subscribeToContestDateChanges(): void {
    // Watch start date changes
    this.addContentForm.get('start_date')?.valueChanges.subscribe(() => {
    });
    // Watch end date changes
    this.addContentForm.get('end_date')?.valueChanges.subscribe(() => {
    });
  }
  private subscribeToLevelChanges(levelGroup: FormGroup, index: number): void {
    levelGroup.get('level_timeline')?.valueChanges.subscribe((value) => {
      if (value) {
      }
    });
    this.subscribeToLevelTypeChanges(levelGroup, index);
  }
  deletePrizeImage(index: number) {
    if (this.imagePreview[index]) {
      this.imagePreview[index].image_code = '';
    }
    const prizeControl = this.prizes.at(index);
    if (prizeControl) {
      prizeControl.get('image_code')?.setValue('');
    }
    const fileInput = document.getElementById(`prizeFileInput${index}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = ''; // Important
    }
  }

  confirmRemovePrize(index: number): void {
    const prizeGroup = this.prizes.at(index) as FormGroup;
    const hasData = Object.keys(prizeGroup.controls).some(
      (key) => prizeGroup.get(key)?.value
    );
    if (hasData) {
      this.showPrizeConfirm(index);
    } else {
      this.removePrize(index);
    }
  }
  removePrizes(index: number): void {
    const prizeGroup = this.prizes.at(index) as FormGroup;
    const prizeId = prizeGroup.get('id')?.value || null;
    this.prizes.removeAt(index);
    this.imagePreview.splice(index, 1);
    this.prizeCategoryOptions.splice(index, 1);
    // agar DB ka prize hai (id present hai) to API call karo
    if (prizeId) {
      this.authService
        .deletebyId('REQUEST_CONTEST_REMOVE_PRIZE', prizeId)
        .subscribe({
          next: () => {
            this.sharedService.createNotification(
              'success',
              'Prize removed successfully'
            );
          },
          error: (error) => {
            this.sharedService.createNotification(
              'error',
              error.error?.message || 'Failed to remove prize'
            );
          },
        });
    }
  }
  showPrizeConfirm(index: number): void {
    this.modal.confirm({
      nzTitle: 'Confirm Removal',
      nzContent:
        'This prize contains data. Are you sure you want to remove it?',
      nzOnOk: () => this.removePrizes(index),
      nzClassName: 'custom-confirm-modal',
      nzCentered: true,
      nzOnCancel: () => { },
    });
  }
  onFormatChange(value: string) {
    // ✅ Don't reset type field if we're in edit mode and loading data
    if (this.isEditMode && this.singleContestData) {
      return;
    }

    // Note: We don't reset the main contest 'type' field (public/private)
    // Only reset 'type' inside levels_of_contest array (level types: text/qr/image)
    const levelsArray = this.addContentForm.get('levels_of_contest');
    if (levelsArray && Array.isArray(levelsArray.value)) {
      const updatedLevels = levelsArray.value.map((level: any) => ({
        ...level,
        type: null
      }));
      levelsArray.setValue(updatedLevels);
    }
  }

  // Level count management methods
  setLevelCount(count: number): void {
    this.selectedLevelCount = count;
    this.customLevelCount = null; // Clear custom input when button is selected
    this.updateLevels(count);
  }

  onCustomLevelInput(value: number): void {
    if (value && value > 0) {
      // Limit to maximum 20 levels
      if (value > 20) {
        this.customLevelCount = 20;
        this.sharedService.createNotification(
          'warning',
          'Maximum 20 levels allowed'
        );
        this.updateLevels(20);
      } else {
        this.selectedLevelCount = null; // Clear button selection when custom input is used
        this.customLevelCount = value;
        this.updateLevels(value);
      }
    }
  }

  // Stepper navigation methods
  nextStep(): void {
    if (this.currentStep < 3) {
      // Validate current step before moving forward
      if (this.validateCurrentStep()) {
        this.currentStep++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Calculate time duration for levels preview
  calculateTimeFromNow(timeline: Date | string | null): string {
    if (!timeline) return 'Timeline not set';

    const contestStartDate = this.addContentForm.get('start_date')?.value;
    const timelineDate = new Date(timeline);

    if (!contestStartDate) {
      // If no contest start date, calculate from now
      const now = new Date();
      const diff = timelineDate.getTime() - now.getTime();
      return this.formatTimeDuration(diff);
    }

    // Calculate from contest start date
    const startDate = new Date(contestStartDate);
    const diff = timelineDate.getTime() - startDate.getTime();
    return this.formatTimeDuration(diff);
  }

  // Format time duration into readable format (days, hours, minutes)
  formatTimeDuration(milliseconds: number): string {
    if (milliseconds < 0) return '0d 0h 0m 0s';

    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }

  private startContestCountdown(): void {
    this.updateContestTimeLeft();

    if (this.contestCountdownIntervalId) {
      clearInterval(this.contestCountdownIntervalId);
    }

    this.contestCountdownIntervalId = setInterval(() => {
      this.updateContestTimeLeft();
    }, 1000);
  }

  private stopContestCountdown(): void {
    if (this.contestCountdownIntervalId) {
      clearInterval(this.contestCountdownIntervalId);
      this.contestCountdownIntervalId = null;
    }
  }

  private updateContestTimeLeft(): void {
    const endDateValue = this.addContentForm.get('end_date')?.value;
    if (!endDateValue) {
      this.contestTimeLeft = '0d 0h 0m 0s';
      return;
    }

    const endDateMs = new Date(endDateValue).getTime();
    if (Number.isNaN(endDateMs)) {
      this.contestTimeLeft = '0d 0h 0m 0s';
      return;
    }

    this.contestTimeLeft = this.formatTimeDuration(endDateMs - Date.now());
  }

  validateCurrentStep(): boolean {
    // For Step 1: Validate contest information fields
    if (this.currentStep === 1) {
      const requiredFields = [
        'name',
        'arabic_name',
        'details',
        'arabic_details',
        'age',
        'gender',
        'location',
        'type',
        'sharingSetting',
        'number_of_winners',
        'format_of_contest',
        'start_date',
        'end_date',
        'prize_announce_date'
      ];

      let isValid = true;
      requiredFields.forEach(field => {
        const control = this.addContentForm.get(field);
        if (control && control.invalid) {
          control.markAsTouched();
          isValid = false;
        }
      });

      return isValid;
    }

    // For Step 2: Validate prizes
    if (this.currentStep === 2) {
      let isValid = true;
      this.prizes.controls.forEach((prize, index) => {
        const prizeGroup = prize as FormGroup;
        if (prizeGroup.invalid) {
          Object.values(prizeGroup.controls).forEach(control => {
            control.markAsTouched();
          });
          isValid = false;
        }
      });

      if (!isValid) {
        this.sharedService.createNotification('error', 'Please fill all prize details before proceeding');
      }

      return isValid;
    }

    return true;
  }

  hasUnsavedChanges(): boolean {
    return this.addContentForm?.dirty || false;
  }

  private markFormAsSaved(): void {
    this.addContentForm.markAsPristine();
    this.addContentForm.markAsUntouched();

    Object.values(this.addContentForm.controls).forEach((control) => {
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isRefresh = event.key === 'F5' || ((event.ctrlKey || event.metaKey) && event.key === 'r');
    if (isRefresh && this.hasUnsavedChanges()) {
      event.preventDefault();
      this.modal.confirm({
        nzTitle: 'Unsaved Changes',
        nzContent: 'You have unsaved changes. Are you sure you want to refresh this page?',
        nzOkText: 'Yes, Refresh',
        nzCancelText: 'Cancel',
        nzCentered: true,
        nzClassName: 'custom-confirm-modal',
        nzOnOk: () => window.location.reload(),
        nzOnCancel: () => { }
      });
    }
  }

  confirmUnsavedChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.confirm({
        nzTitle: 'Unsaved Changes',
        nzContent: this.leavePageWarningMessage,
        nzOkText: 'Yes, Leave',
        nzCancelText: 'Cancel',
        nzCentered: true,
        nzClassName: 'custom-confirm-modal',
        nzOnOk: () => resolve(true),
        nzOnCancel: () => resolve(false)
      });
    });
  }

}

